import React, { useState } from 'react'
import { css } from '@emotion/core'
import { trackEvent } from '@fs/zion-analytics'
import { isIosDevice, isAndroidDevice } from '@fs/zion-frontend-friends'
import { Bleed, Card, DialogOverlay, Image, useOverlay } from '@fs/zion-ui'

import playButtonImage from './play-button.webp'
import playButtonImageV2 from './play-button_v2.webp'

const playButtonImageCss = css`
  z-index: 1;
  position: absolute;
  bottom: 14px;
  inset-inline-start: 14px;
  background-image: url(${playButtonImage});
  background-position: center;
  background-size: cover;
  width: 60px;
  height: 60px;
`

const durationContainerCss = css`
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  color: black;

  position: absolute;
  bottom: 14px;
  inset-inline-end: 14px;

  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;

  font-size: 14px;

  &[data-v2] {
    box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.25);
  }
`

const bottomFadeCss = css`
  position: absolute;
  bottom: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 25%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(4, 18, 2, 0) 100%);
`

export default function VideoPopup({
  children,
  duration,
  isYoutube = false,
  CustomButton,
  thumbnail,
  title,
  url,
  v2,
  ...props
}) {
  const [play, setPlay] = useState(false)
  const overlay = useOverlay()
  const isMobile = isAndroidDevice() || isIosDevice()

  const playVideo = () => {
    trackEvent({ event_name: `Button:Video opened - ${title}:Click` })
    setPlay(true)
  }

  const openOverlay = () => {
    trackEvent({ event_name: `Button:Video opened - ${title}:Click` })
    overlay.handleClick()
  }

  const containerProps = play && isMobile ? {} : { onClick: isMobile ? () => playVideo() : openOverlay }

  return (
    <>
      {CustomButton ? (
        <CustomButton {...props} onClick={openOverlay} />
      ) : (
        <Card {...props} {...containerProps}>
          {children}
          <Bleed all style={{ position: 'relative' }}>
            {isMobile && play && (
              <Video title={title} thumbnail={thumbnail} isYoutube={isYoutube} url={url} playsInline />
            )}
            {(!play || !isMobile) && (
              <>
                <Image src={thumbnail} alt={title} />
                {v2 ? (
                  <OverlayV2 duration={duration} />
                ) : (
                  <>
                    <div css={playButtonImageCss} />
                    <div css={durationContainerCss}>
                      <span>{duration}</span>
                    </div>
                    <div css={bottomFadeCss} />
                  </>
                )}
              </>
            )}
          </Bleed>
        </Card>
      )}

      <DialogOverlay {...overlay} title={title} size="xxl" fullscreen={false}>
        <Video title={title} thumbnail={thumbnail} isYoutube={isYoutube} url={url} />
      </DialogOverlay>
    </>
  )
}

function Video({ title, thumbnail, isYoutube, url, playsInline }) {
  return (
    <>
      {isYoutube ? (
        <iframe
          title={title}
          src={url}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            height: 'calc(var(--cell-width) * 0.5625)', // 16:9 aspect ratio
            width: '100%',
            border: 'none',
          }}
        />
      ) : (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          disablePictureInPicture
          playsInline={playsInline}
          autoPlay
          aria-label={title}
          controls
          poster={thumbnail}
          title={title}
          style={{
            height: 'auto',
            width: '100%',
          }}
        >
          <source src={url} type="video/mp4" />
        </video>
      )}
    </>
  )
}

const overlayV2Css = css`
  width: 100%;
  height: 100%;

  img {
    position: absolute;
    top: 50%;
    inset-inline-start: 50%;
    transform: translate(-50%, -50%);
  }
`

function OverlayV2({ duration }) {
  return (
    <div css={overlayV2Css}>
      <img src={playButtonImageV2} alt="Play button" />
      <div css={durationContainerCss} data-v2>
        <span>{duration}</span>
      </div>
    </div>
  )
}
